// Pure text-parsing helpers for the bulk-add feature, shared by the parsing
// backend (packages/backend) and the frontend MSW mock so there is exactly one
// implementation. Dependency-free and pure, hence trivially unit-testable.
import type { WordTag } from '../schemas/noyau.js';

/** One word extracted from the pasted text: a title plus any pre-filled tags. */
export type ParsedWord = {
  title: string;
  tags: WordTag[];
};

/**
 * Normalize a title for conflict comparison: lowercased, accent-stripped, with
 * runs of whitespace collapsed to a single space and the ends trimmed. Two
 * titles "conflict" when their normalized forms are equal — this is the single
 * identity key shared by batch-vs-batch and batch-vs-DB detection.
 */
export function normalizeTitle(title: string): string {
  return title.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').replace(/\s+/g, ' ').trim();
}

/**
 * Strip the trailing parenthetical group(s) from a line and return both the
 * cleaned title and the tag labels they contained.
 *
 * Only groups anchored to the END of the line are consumed, peeled off one at a
 * time from the right — so `Mot (a, b) (c)` yields tags [a, b, c] and title
 * "Mot", while a mid-line `(x)` (e.g. "Mot (x) suite") is left untouched. Each
 * group is comma-split; empty fragments are dropped. Tag order follows the
 * left-to-right reading order of the original line.
 */
export function extractTrailingTags(line: string): {
  title: string;
  labels: string[];
  /** Whether any peeled group's inner content was blank, e.g. `Mot ()`. */
  hadEmptyGroup: boolean;
} {
  let rest = line.trimEnd();
  const groups: string[][] = [];
  let hadEmptyGroup = false;
  // Peel `(...)` groups off the end repeatedly. `[^()]*` keeps it to flat,
  // non-nested groups — enough for the agreed `Mot (tag)` shorthand.
  const trailing = /\s*\(([^()]*)\)\s*$/;
  let match = trailing.exec(rest);
  while (match) {
    const inner = match[1] ?? '';
    if (inner.trim() === '') hadEmptyGroup = true;
    const labels = inner
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    groups.push(labels);
    rest = rest.slice(0, match.index).trimEnd();
    match = trailing.exec(rest);
  }
  // `groups` was collected right-to-left; reverse to restore reading order.
  return { title: rest.trim(), labels: groups.reverse().flat(), hadEmptyGroup };
}

/**
 * Parse a pasted blob into candidate words: one trimmed, non-empty line becomes
 * one word. Blank lines are skipped. The `Mot (tag)` shorthand pre-fills tags
 * (see {@link extractTrailingTags}); parsed tags are plain free-form labels with
 * no noyau colour attached. Duplicate tag labels within a single word are
 * de-duplicated; lines that are empty once their trailing parentheses are
 * removed are dropped.
 */
export function parseBulkInput(text: string): ParsedWord[] {
  const words: ParsedWord[] = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const { title, labels } = extractTrailingTags(line);
    if (!title) continue;
    const seen = new Set<string>();
    const tags: WordTag[] = [];
    for (const label of labels) {
      if (seen.has(label)) continue;
      seen.add(label);
      tags.push({ label });
    }
    words.push({ title, tags });
  }
  return words;
}

/** The two malformed trailing-tag-group cases the input phase flags advisorily. */
export type LineDiagnosticKind = 'unbalancedParens' | 'emptyTagGroup';

export type LineDiagnostic = {
  kind: LineDiagnosticKind;
  message: string;
  /** The corrected line text, applied verbatim when the user accepts the fix. */
  suggestion: string;
};

/**
 * Diagnose a single input line for the two malformed-syntax cases the parser
 * otherwise absorbs or ignores silently: an empty trailing tag group
 * (`Mot ()`), and parens {@link extractTrailingTags} can't cleanly consume —
 * unbalanced, or nested (its `[^()]*` core only matches a flat group) — which
 * leave stray `(`/`)` baked into the title. Advisory only: this never blocks
 * parsing, it just powers the input phase's inline hints (see BulkInput).
 */
export function diagnoseBulkLine(line: string): LineDiagnostic | undefined {
  const trimmed = line.trim();
  if (!trimmed) return undefined;

  const { title, hadEmptyGroup } = extractTrailingTags(trimmed);

  if (/[()]/.test(title)) {
    const opens = (trimmed.match(/\(/g) ?? []).length;
    const closes = (trimmed.match(/\)/g) ?? []).length;
    const suggestion =
      opens > closes
        ? `${trimmed}${')'.repeat(opens - closes)}`
        : trimmed.replace(/[()]/g, '').replace(/\s+/g, ' ').trim();
    return {
      kind: 'unbalancedParens',
      message: 'Parenthèses non valides : ce groupe ne sera pas reconnu comme des tags.',
      suggestion,
    };
  }

  if (hadEmptyGroup) {
    return { kind: 'emptyTagGroup', message: 'Groupe de tags vide.', suggestion: title };
  }

  return undefined;
}
